import React, { useLayoutEffect, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { setTag } from '@sentry/react';
import {
  ReactLocation,
  createMemoryHistory,
  Router,
  Outlet as LegacyOutlet,
  useNavigate as useNavigateLegacy,
} from '@tanstack/react-location';
import { ErrorBoundary, MainBoundary } from '@frontend/error-boundary';
import { useEventListener } from '@frontend/event';
import {
  WeaveRoute,
  defaultMissingModuleFallback,
  defaultModuleFetchErrorFallback,
} from '@frontend/file-based-routing';
import { useScopePolicy } from '@frontend/scope-access';
import { SettingsPanelLayout } from '@frontend/settings-panel-engine';
import {
  useInterRouter,
  settingsRouteLookup,
  useSettingsPathLabelMap,
  useSettingsNavigate,
  StaticSettingsRoutes,
  getHashParams,
  setHashWithParams,
} from '@frontend/settings-routing';
import { useNavigate, useRouter } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { SlideUpModal, useSlideUpModal } from '@frontend/design-system';
import { SettingsNav } from '../../layout';
import { useSettingsNavSize } from '../../layout/settings-nav/use-settings-nav-size';
import { buildMappedRoutesLookup, getWeaveRoutes, getWeaveSettingsRoutes } from '../../utils/routing-legacy/routes';
import { NoScopeAccess } from '../no-scope-access';
import { SettingsTopBar } from './settings-top-bar';

const memoryHistory = createMemoryHistory();
const location = new ReactLocation({ history: memoryHistory });

const routes = getWeaveSettingsRoutes({
  queryClient: undefined,
  missingModuleFallback: defaultMissingModuleFallback,
  moduleFetchErrorFallback: defaultModuleFetchErrorFallback,
});

const weaveRoutes = getWeaveRoutes({
  queryClient: undefined,
  missingModuleFallback: defaultMissingModuleFallback,
  moduleFetchErrorFallback: defaultModuleFetchErrorFallback,
});

const isValidRoute = (path: string) => {
  const cleanedPath = path.replace('/', '');
  return cleanedPath.startsWith('settings/');
};

const flattenRoutes = (routes: typeof weaveRoutes, prefix = ''): string[] => {
  return routes.reduce((acc, route) => {
    if (!route.path) return acc;

    const fullPath = prefix + route.path;
    if (isValidRoute(fullPath)) {
      acc.push(route.path.endsWith('/') ? fullPath : fullPath + '/');
    }
    if (route.children) {
      acc.push(...flattenRoutes(route.children as typeof weaveRoutes, route.path));
    }
    return acc;
  }, [] as string[]);
};

const allRoutes = flattenRoutes(weaveRoutes);

settingsRouteLookup.build(allRoutes);

export const SettingsModal = () => {
  return (
    <>
      <SettingsModalContainer>
        <ModalContent>
          <SettingsNav />
        </ModalContent>
      </SettingsModalContainer>
      <MainRouterNavigate />
    </>
  );
};

/**
 * TODO: understand this better
 * When the main router path changes:
 *  if it's a settings path
 *    - we close the settings modal
 *  then wen navigate to the new path (for some reason?)
 *  then set the main router path to empty (for some reason?)
 */
const MainRouterNavigate = () => {
  const { closeSettings, isSettingsOpen, setMainRouterPath, mainRouterPath } = useInterRouter([
    'closeSettings',
    'isSettingsOpen',
    'setMainRouterPath',
    'mainRouterPath',
  ]);

  const navigate = useNavigate();

  useEffect(() => {
    if (mainRouterPath.path) {
      if (isSettingsOpen) {
        closeSettings();
      }
      navigate({ to: mainRouterPath.path, search: mainRouterPath.search, replace: mainRouterPath.replace });
      setMainRouterPath({ path: '' });
    }
  }, [mainRouterPath.path]);

  return null;
};

export const SettingsModalContainer = ({ children }: { children: React.ReactNode }) => {
  const { settingsRouterPath, closeSettings, isSettingsOpen } = useInterRouter([
    'settingsRouterPath',
    'closeSettings',
    'isSettingsOpen',
  ]);
  const pathLabelMap = useSettingsPathLabelMap();
  const { navigate } = useSettingsNavigate();
  const { isSmallerThanDesktop } = useSettingsNavSize();

  const { modalProps } = useSlideUpModal({
    open: !!settingsRouterPath.path && isSettingsOpen,
    onOpenChange: (open) => {
      if (!open) {
        closeSettings();
      }
    },
    outsidePress: (event) => (event.target as HTMLElement).id === 'vertical-slide-modal-backdrop',
  });

  const path = settingsRouterPath.path.replace(/^\/+/g, '');

  //syncs the settings path to the hash
  useEffect(() => {
    if (isSettingsOpen) {
      setHashWithParams(path, settingsRouterPath.search);
      setTag('settings.route', path);
      return;
    }
    setTag('settings.route', undefined);
  }, [isSettingsOpen, path, settingsRouterPath.search]);

  useEffect(() => {
    //if there is a settings hash on the initial load, navigate to that hash
    if (
      window.location.hash.includes('#settings/') &&
      !isSettingsOpen &&
      path !== window.location.hash.replace('#settings/', '')
    ) {
      const newPath = window.location.hash.replace('#settings/', '').split('?')[0];
      navigate({ to: newPath as StaticSettingsRoutes, search: getHashParams() });
    }
  }, []);

  useEventListener(
    'hashchange',
    (event) => {
      const newHash = new URL(event.newURL).hash;
      if (!newHash) {
        closeSettings();
        return;
      }
      if (newHash.includes('#settings/')) {
        const newSettingsPath = newHash.replace('#settings/', '');

        const settingsBasePath = settingsRouterPath.path.replace(/^\/+/g, '');
        let settingsPathWithParams = settingsBasePath;

        if (settingsRouterPath.search) {
          const settingsSearchParams = new URLSearchParams(
            settingsRouterPath.search as Record<string, string>
          ).toString();
          settingsPathWithParams = settingsSearchParams
            ? `${settingsBasePath}?${settingsSearchParams}`
            : settingsBasePath;
        }

        if (settingsPathWithParams !== newSettingsPath) {
          navigate({ to: newSettingsPath as StaticSettingsRoutes });
        }
      }
    },
    true
  );

  useEffect(() => {
    const mappedRoutes = buildMappedRoutesLookup(pathLabelMap);
    settingsRouteLookup.buildFuse(mappedRoutes);
  }, []);

  return (
    <SlideUpModal
      {...modalProps}
      css={[
        css`
          background: ${theme.colors.neutral90};
          overflow: hidden;
        `,
        isSmallerThanDesktop &&
          ((theme) => css`
            height: calc(100% - ${theme.heightOffset}px);
            border-radius: 0;
          `),
      ]}
    >
      <Router routes={routes as WeaveRoute[]} location={location} basepath='/settings'>
        <SettingsTopBar />
        <SlideUpModal.Content
          data-settings-modal-content
          css={css`
            background: ${theme.colors.neutral10};
            overflow: hidden;
          `}
        >
          <SyncMainRouterToSettingsRouter />
          <SyncSettingsRouterToMainRouter />
          {children}
        </SlideUpModal.Content>
      </Router>
    </SlideUpModal>
  );
};

/**
 *
 * If the new main router path is changed while the settings is open (likely from a link in the settings)
 */
const SyncMainRouterToSettingsRouter = () => {
  const { settingsRouterPath: _settingsRouterPath, closeSettings } = useInterRouter([
    'settingsRouterPath',
    'closeSettings',
  ]);
  const mainRouter = useRouter();
  const path = mainRouter.state.location.pathname;
  const prev = useRef(path);

  useEffect(() => {
    if (path === prev.current) {
      return;
    }
    closeSettings();
  }, [path]);
  return null;
};

const SyncSettingsRouterToMainRouter = () => {
  const { settingsRouterPath, isSettingsOpen } = useInterRouter(['settingsRouterPath', 'isSettingsOpen']);
  const navigate = useNavigateLegacy();

  useLayoutEffect(() => {
    if (settingsRouterPath.path && isSettingsOpen) {
      navigate({ to: settingsRouterPath.path, search: settingsRouterPath.search, replace: settingsRouterPath.replace });
    }
  }, [settingsRouterPath.path, isSettingsOpen]);
  return null;
};

const ModalContent = ({ children }: { children: React.ReactNode }) => {
  const { settingsRouterPath } = useInterRouter(['settingsRouterPath']);
  const { getDecisionForPath } = useScopePolicy();

  const normalizedSettingsPath = settingsRouterPath.path.startsWith('/')
    ? settingsRouterPath.path
    : `/${settingsRouterPath.path}`;
  const policyPath = settingsRouterPath.path ? `/settings${normalizedSettingsPath}` : '/settings';
  const blockedDecision = getDecisionForPath(policyPath);
  const isBlockedByScope = !!settingsRouterPath.path && blockedDecision.availability !== 'available';

  return (
    <div
      css={css`
        height: 100%;
        display: flex;
        width: 100%;
      `}
    >
      {children}
      <main
        css={css`
          width: 100%;
          overflow-y: auto;
        `}
      >
        <ErrorBoundary
          tier='route'
          feature='settings-modal-outlet'
          team='frontend-ops'
          resetKeys={[settingsRouterPath.path]}
          fallback={MainBoundary}
        >
          {isBlockedByScope ? <NoScopeAccess dataTestId='settings-scope-policy-blocked-route' /> : <LegacyOutlet />}
        </ErrorBoundary>
      </main>
      <SettingsPanelLayout />
    </div>
  );
};
